import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Database,
  Upload,
  Download,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  Users,
  Car,
  MapPin,
  Calendar,
  Loader2,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useSeries } from '../../App';
import { useApi } from '../../contexts/ApiContext';

const dataSourceIcons = {
  drivers: { icon: Users, color: 'bg-blue-100 text-blue-600' },
  teams: { icon: Car, color: 'bg-purple-100 text-purple-600' },
  tracks: { icon: MapPin, color: 'bg-emerald-100 text-emerald-600' },
  schedule: { icon: Calendar, color: 'bg-amber-100 text-amber-600' },
  points: { icon: Database, color: 'bg-red-100 text-red-600' },
};

// External data sources
const externalSources = [
  {
    id: 'mylaps',
    name: 'MyLaps',
    description: 'Transponder and timing data',
    status: 'connected',
    lastSync: '2024-01-15T08:00:00',
  },
  {
    id: 'dirtcar',
    name: 'DIRTcar Database',
    description: 'Official DIRTcar driver registry',
    status: 'connected',
    lastSync: '2024-01-14T22:00:00',
  },
  {
    id: 'orbits',
    name: 'Orbits API',
    description: 'Legacy data and historical records',
    status: 'disconnected',
    lastSync: null,
  },
];

function DataSourceCard({ source }) {
  const iconConfig = dataSourceIcons[source.id] || { icon: Database, color: 'bg-slate-100 text-slate-600' };
  const Icon = iconConfig.icon;
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    // Simulate sync - in the future this would call an actual sync API
    await new Promise(resolve => setTimeout(resolve, 2000));
    setSyncing(false);
  };

  const statusBadge = {
    synced: <Badge variant="success">Synced</Badge>,
    outdated: <Badge variant="warning">Outdated</Badge>,
    not_synced: <Badge variant="neutral">Not Synced</Badge>,
    error: <Badge variant="danger">Error</Badge>,
  };

  return (
    <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${iconConfig.color}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-slate-800">{source.name}</h3>
          {statusBadge[source.status]}
        </div>
        <p className="text-sm text-slate-500 mb-1">{source.description}</p>
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <span>{source.recordCount.toLocaleString()} records</span>
        </div>
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleSync}
        disabled={syncing}
        leftIcon={syncing ? Loader2 : RefreshCw}
        className={syncing ? 'animate-spin-icon' : ''}
      >
        {syncing ? 'Syncing...' : 'Sync'}
      </Button>
    </div>
  );
}

function ExternalSourceCard({ source }) {
  const isConnected = source.status === 'connected';

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
        <div>
          <div className="font-medium text-slate-700">{source.name}</div>
          <div className="text-xs text-slate-500">{source.description}</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-400">
          {isConnected ? `Synced ${formatDate(source.lastSync)}` : 'Not connected'}
        </span>
        <Button variant="ghost" size="sm">
          {isConnected ? 'Configure' : 'Connect'}
        </Button>
      </div>
    </div>
  );
}

export default function SeriesConfig() {
  useParams();
  const currentSeries = useSeries();
  const api = useApi();
  const [syncingAll, setSyncingAll] = useState(false);

  const { data: dataSources = [], isLoading: loading } = useQuery({
    queryKey: ['preload-stats', currentSeries?.dbId],
    queryFn: async () => {
      const result = await api.series.getPreloadStats(currentSeries.dbId);
      if (!result.success || !result.data?.dataSources) throw new Error(result.message || 'Failed to load preload stats');
      return result.data.dataSources;
    },
    enabled: !!currentSeries?.dbId,
  });

  const handleSyncAll = async () => {
    setSyncingAll(true);
    await new Promise(resolve => setTimeout(resolve, 3000));
    setSyncingAll(false);
  };

  const syncedCount = dataSources.filter(s => s.status === 'synced').length;
  const totalCount = dataSources.length;

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Database className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="page-title">Preload Series Data</h1>
            <p className="page-subtitle">Import and sync data from external sources</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            leftIcon={Download}
          >
            Export All
          </Button>
          <Button
            variant="primary"
            leftIcon={syncingAll ? Loader2 : RefreshCw}
            onClick={handleSyncAll}
            disabled={syncingAll}
          >
            {syncingAll ? 'Syncing All...' : 'Sync All Data'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Data Sources */}
          <Card>
            <CardHeader>
              <CardTitle>Data Sources</CardTitle>
              <CardDescription>Sync data from the master database</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : dataSources.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No data sources available
                </div>
              ) : (
                dataSources.map(source => (
                  <DataSourceCard key={source.id} source={source} />
                ))
              )}
            </CardContent>
          </Card>

          {/* Import Options */}
          <Card>
            <CardHeader>
              <CardTitle>Import Data</CardTitle>
              <CardDescription>Import data from files or external systems</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border-2 border-dashed border-slate-200 rounded-lg text-center hover:border-rms-accent hover:bg-slate-50 transition-colors cursor-pointer">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <div className="font-medium text-slate-700">Upload CSV</div>
                  <div className="text-sm text-slate-500">Import drivers, teams, or results</div>
                </div>
                <div className="p-4 border-2 border-dashed border-slate-200 rounded-lg text-center hover:border-rms-accent hover:bg-slate-50 transition-colors cursor-pointer">
                  <Database className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <div className="font-medium text-slate-700">Connect Database</div>
                  <div className="text-sm text-slate-500">Link to external data source</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Sync Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                Sync Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Data Sources</span>
                    <span className="text-sm font-medium text-slate-800">
                      {syncedCount}/{totalCount} synced
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-emerald-500 h-2 rounded-full transition-all"
                      style={{ width: totalCount > 0 ? `${(syncedCount / totalCount) * 100}%` : '0%' }}
                    />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 text-emerald-500" />
                      {syncedCount} up to date
                    </span>
                    <span className="flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 text-amber-500" />
                      {dataSources.filter(s => s.status === 'outdated').length} outdated
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* External Connections */}
          <Card>
            <CardHeader>
              <CardTitle>External Connections</CardTitle>
              <CardDescription>Third-party data sources</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {externalSources.map(source => (
                <ExternalSourceCard key={source.id} source={source} />
              ))}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <button className="w-full flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors">
                <span className="text-sm font-medium text-slate-700">Validate Data Integrity</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
              <button className="w-full flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors">
                <span className="text-sm font-medium text-slate-700">Clear Cache</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
              <button className="w-full flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors">
                <span className="text-sm font-medium text-slate-700">View Sync Logs</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
